import React, { useEffect, useMemo, useState } from "react";
import Head from "next/head";
import Modal from "./Modal";
import { useArticleMeta } from "./ArticleMetaContext";

function extractYoutubeId(input) {
	if (!input) return "";

	if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
		return input;
	}

	try {
		const parsedUrl = new URL(input);

		if (parsedUrl.hostname.includes("youtu.be")) {
			const id = parsedUrl.pathname.replace("/", "").trim();
			return /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : "";
		}

		const v = parsedUrl.searchParams.get("v");

		if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) {
			return v;
		}

		const shortsMatch = parsedUrl.pathname.match(/\/shorts\/([a-zA-Z0-9_-]{11})/);
		if (shortsMatch?.[1]) {
			return shortsMatch[1];
		}

		const liveMatch = parsedUrl.pathname.match(/\/live\/([a-zA-Z0-9_-]{11})/);
		if (liveMatch?.[1]) {
			return liveMatch[1];
		}

		const embedMatch = parsedUrl.pathname.match(/\/embed\/([a-zA-Z0-9_-]{11})/);
		if (embedMatch?.[1]) {
			return embedMatch[1];
		}
	} catch {}

	return "";
}

function normalizeUploadDate(datePublication, hours) {
	if (!datePublication) return undefined;

	const date = String(datePublication).trim();
	const time = String(hours || "00:00:00").trim();

	if (!date) return undefined;

	if (date.includes("T")) {
		return date;
	}

	return `${date}T${time}+02:00`;
}

function removeEmptyFields(object) {
	return Object.fromEntries(
		Object.entries(object).filter(([, value]) => {
			if (value === undefined || value === null || value === "") return false;
			if (Array.isArray(value) && value.length === 0) return false;
			return true;
		}),
	);
}

function loadImage(src) {
	return new Promise((resolve, reject) => {
		const img = new Image();

		img.onload = () => {
			resolve({
				src,
				width: img.naturalWidth,
				height: img.naturalHeight,
			});
		};

		img.onerror = () => {
			reject(new Error(`Nie udało się załadować miniaturki: ${src}`));
		};

		img.decoding = "async";
		img.src = src;
	});
}

function withTimeout(promise, ms = 3500) {
	return Promise.race([
		promise,
		new Promise((_, reject) => {
			window.setTimeout(() => reject(new Error("Timeout ładowania miniaturki")), ms);
		}),
	]);
}

function isBadYoutubeThumbnail(result) {
	if (!result) return true;

	// YouTube przy braku maxresdefault potrafi zwrócić placeholder 120x90.
	if (result.width <= 120 || result.height <= 90) {
		return true;
	}

	// Miniaturki mniejsze niż 320 px zwykle wyglądają słabo na szerokim artykule.
	if (result.width < 320) {
		return true;
	}

	return false;
}

function getAspectScore(result) {
	const aspect = result.width / result.height;
	const distanceFrom169 = Math.abs(aspect - 16 / 9);

	// Preferuj 16:9, ale nie zabijaj fallbacków 4:3 typu hqdefault.
	return Math.max(0, 1 - distanceFrom169);
}

function getThumbnailScore(result) {
	const area = result.width * result.height;
	const aspectBonus = getAspectScore(result) * 500000;

	return area + aspectBonus;
}

function getYoutubeThumbnailCandidates(videoId, customThumbnail) {
	const candidates = [];

	if (customThumbnail) {
		candidates.push(customThumbnail);
	}

	if (!videoId) {
		return candidates;
	}

	// Najpierw najlepsze jakościowo warianty. hq720 działa dla wielu filmów,
	// mimo że nie jest tak oficjalnie przewidywalny jak maxres/sd/hq.
	candidates.push(
		`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
		`https://i.ytimg.com/vi_webp/${videoId}/maxresdefault.webp`,
		`https://i.ytimg.com/vi/${videoId}/hq720.jpg`,
		`https://i.ytimg.com/vi_webp/${videoId}/hq720.webp`,
		`https://i.ytimg.com/vi/${videoId}/sddefault.jpg`,
		`https://i.ytimg.com/vi_webp/${videoId}/sddefault.webp`,
		`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
		`https://i.ytimg.com/vi_webp/${videoId}/hqdefault.webp`,
		`https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`,
		`https://i.ytimg.com/vi/${videoId}/default.jpg`,
	);

	return [...new Set(candidates.filter(Boolean))];
}

function ThumbImage({ videoId, alt, className, customThumbnail }) {
	const candidates = useMemo(() => {
		return getYoutubeThumbnailCandidates(videoId, customThumbnail);
	}, [videoId, customThumbnail]);

	const [src, setSrc] = useState("");
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;

		async function pickThumbnail() {
			if (!videoId && !customThumbnail) {
				setSrc("");
				setIsLoading(false);
				return;
			}

			setSrc("");
			setIsLoading(true);

			const checked = [];

			await Promise.all(
				candidates.map(async (candidate) => {
					try {
						const result = await withTimeout(loadImage(candidate));
						const bad = isBadYoutubeThumbnail(result);

						checked.push({
							...result,
							bad,
							score: bad ? 0 : getThumbnailScore(result),
						});
					} catch (error) {
						checked.push({
							src: candidate,
							error: error.message,
							bad: true,
							score: 0,
						});
					}
				}),
			);

			if (cancelled) {
				return;
			}

			const best = checked
				.filter((item) => !item.bad)
				.sort((a, b) => b.score - a.score)[0];

			if (best?.src) {
				setSrc(best.src);
				setIsLoading(false);
				return;
			}

			setSrc(candidates[candidates.length - 1] || "");
			setIsLoading(false);
		}

		pickThumbnail();

		return () => {
			cancelled = true;
		};
	}, [videoId, customThumbnail, candidates]);

	if (!videoId && !customThumbnail) {
		return <div className="image-placeholder">Nieprawidłowy link YouTube</div>;
	}

	if (isLoading || !src) {
		return <div className="image-placeholder">Ładowanie miniaturki...</div>;
	}

	return (
		<img
			className={className}
			src={src}
			alt={alt}
			loading="lazy"
			decoding="async"
			referrerPolicy="no-referrer"
		/>
	);
}

const ArticleVideo = ({ url, src, title: titleProp, thumbnail, thumbnailUrl, poster }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);

	const articleMeta = useArticleMeta();
	const videoUrl = url || src || "";
	const videoId = extractYoutubeId(videoUrl);
	const customThumbnail = thumbnail || thumbnailUrl || poster || "";

	const title = titleProp || articleMeta.title || articleMeta.seoTitle || "Wideo";

	const description =
		articleMeta.description || articleMeta.seoDescription || `Materiał wideo: ${title}`;

	const uploadDate = normalizeUploadDate(
		articleMeta.datePublication || articleMeta.datePublished || articleMeta.date,
		articleMeta.hours,
	);

	const fallbackThumbnailUrl = videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : "";
	const structuredThumbnailUrl = customThumbnail || fallbackThumbnailUrl;
	const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : "";

	const videoJsonLd =
		videoId && videoUrl
			? removeEmptyFields({
					"@context": "https://schema.org",
					"@type": "VideoObject",
					name: title,
					description,
					thumbnailUrl: structuredThumbnailUrl ? [structuredThumbnailUrl] : undefined,
					uploadDate,
					embedUrl,
					contentUrl: videoUrl,
					publisher: {
						"@type": "Organization",
						name: "Przyjazne Deklaracje",
						logo: {
							"@type": "ImageObject",
							url: "https://przyjazne-deklaracje.pl/resources/images/logo/przyjazne-deklaracje/logo-desktop.svg",
						},
					},
				})
			: null;

	const modalData = {
		modalTitle: title,
		modalUrl: videoUrl,
	};

	const openModal = () => {
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
	};

	return (
		<>
			{videoJsonLd && (
				<Head>
					<script
						type="application/ld+json"
						dangerouslySetInnerHTML={{
							__html: JSON.stringify(videoJsonLd).replace(/</g, "\\u003c"),
						}}
					/>
				</Head>
			)}

			<div
				className="imageWrapper articleVideo-wrapper"
				onClick={openModal}
				onKeyDown={(event) => {
					if (event.key === "Enter" || event.key === " ") {
						event.preventDefault();
						openModal();
					}
				}}
				role="button"
				tabIndex={0}
				aria-label={`Otwórz wideo: ${title}`}
				style={{ cursor: "pointer", position: "relative" }}
			>
				<div className="imageShadow">
					<img
						className="imageIcon"
						src="https://przyjazne-deklaracje.pl/resources/images/icons/general/files/icon-play-video.svg"
						alt=""
						aria-hidden="true"
					/>
				</div>

				<ThumbImage
					videoId={videoId}
					customThumbnail={customThumbnail}
					alt={title}
					className="image articleVideo-thumbnail"
				/>
			</div>

			{isModalOpen && <Modal image={modalData} closeModal={closeModal} />}
		</>
	);
};

export default ArticleVideo;
